import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "@shared/lib/api";

interface SearchResult {
  record_type: "child" | "parent" | "employee" | "director" | "lead" | "classroom" | "invoice" | "incident_report";
  id: number;
  display_name: string;
  subtitle: string;
  rank: number;
}

interface SearchResponse {
  results: SearchResult[];
  gated_count: number;
  query: string;
}

export function useGlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [gatedCount, setGatedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    setGatedCount(0);
    setLoading(false);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    // Clear previous timer
    if (timerRef.current) clearTimeout(timerRef.current);

    // Abort in-flight request
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setGatedCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setGatedCount(0);

    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;

      apiFetch<SearchResponse>(
        `/api/v1/search?q=${encodeURIComponent(trimmed)}`,
        { signal: controller.signal }
      )
        .then(({ data }) => {
          if (!controller.signal.aborted) {
            setResults(data.results);
            setGatedCount(data.gated_count ?? 0);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        });
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query, isOpen]);

  return { query, setQuery, results, gatedCount, loading, isOpen, open, close };
}

export type { SearchResult };
